import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('servio_token'));
  const [loading, setLoading] = useState(true);

  // Logout function - memoized so it can be used in interceptor
  const logout = useCallback(() => {
    localStorage.removeItem('servio_token');
    setToken(null);
    setUser(null);
  }, []);

  // Set up axios interceptor to handle 401 errors globally.
  // Only auto-logout when the 401 is genuinely a token/auth-state failure
  // (e.g., expired or invalid JWT). Permission-denied or role-locked endpoints
  // can also return 401, and those must NOT log the user out.
  useEffect(() => {
    const isAuthFailure = (error) => {
      if (!error?.response || error.response.status !== 401) return false;
      const detail = (error.response.data?.detail || '').toString().toLowerCase();
      // Treat as auth failure if the detail explicitly mentions token/auth issues,
      // OR if the request was an auth endpoint (where 401 is always identity-related).
      const url = (error.config?.url || '').toLowerCase();
      const authEndpoint = url.includes('/auth/me') || url.includes('/auth/refresh');
      const tokenWords = ['token', 'expired', 'invalid', 'unauthorized', 'not authenticated', 'credentials'];
      const looksLikeTokenError = tokenWords.some(w => detail.includes(w));
      return authEndpoint || looksLikeTokenError;
    };

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (isAuthFailure(error)) {
          // Token expired/invalid — auto logout and redirect.
          logout();
          if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
            window.location.href = '/login?session_expired=true';
          }
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [logout]);

  const fetchUser = async (authToken) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setUser(response.data);
      return response.data;
    } catch (error) {
      localStorage.removeItem('servio_token');
      setToken(null);
      setUser(null);
      return null;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        await fetchUser(token);
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  // Refresh user data (call after profile updates)
  const refreshUser = async () => {
    if (token) {
      return await fetchUser(token);
    }
    return null;
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('servio_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, fullName, userType = 'server') => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      full_name: fullName,
      user_type: userType
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('servio_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
